import {computed, ref} from "vue";
import {defineStore} from "pinia";
import axios from "axios";
import {toast} from "vue3-toastify";
import {Model3D, Process} from "../js/interface";
import {CommonStore} from "../store/common-store";
import {OptionsStore} from "../store/options-store";
import {EstimateStore} from "../store/estimate-store";
import {AnalysisStatus} from "@/js/types";
import {createTippySingle} from "../js/utils";

/******************************************
 * 하이쓰리디 - 모델 관리 스토어
 ******************************************/
export const ModelStore = defineStore("Model", () => {
  const models = ref(new Array<Model3D>());
  const currentModel = ref({} as Model3D);

  const totalCost = computed(() => {
    let cost = 0;

    for (let i = 0; i < models.value.length; i++) {
      cost += models.value[i].modelCost * models.value[i].modelCount;
    }

    return cost;
  });

  const totalQua = computed(() => {
    let qua = 0;

    for (let i = 0; i < models.value.length; i++) {
      qua += models.value[i].modelCount;
    }

    return qua;
  });

  const getCurrentModel = computed(() => {
    return currentModel;
  });

  function createModel(
    id: string,
    estimateId: number,
    name: string,
    modelKey: string,
    file: any,
    signedUrl: string
  ) {
    const optionsStore = OptionsStore();

    const materials = optionsStore.materials.filter((m) => {
      if (optionsStore.processes[0].id === m.processId) {
        return m;
      }
    });

    const model: Model3D = {
      id: id,
      estimateId: estimateId,
      name: name,
      modelKey: modelKey,
      modelCost: 0,
      modelTotalCost: 0,
      modelCount: 1,
      modelProcessId: optionsStore.processes[0].id,
      modelMaterialId: materials[0].id,
      modelAnalysis: AnalysisStatus.Processing,
      modelPaint: false,
      file: file,
      signedUrl: signedUrl,
      isUploaded: false,
      modelUnit: "mm",
      modelSizeX: 0,
      modelSizeY: 0,
      modelSizeZ: 0,
      modelVolume: 0,
      analysis: [],
    };

    return model;
  }

  function addModel(model: Model3D) {
    models.value.push(model);
  }

  function removeModel(modelId: string) {
    let idx = -1;

    for (let i = 0; i < models.value.length; i++) {
      if (models.value[i].id === modelId) {
        idx = i;
        break;
      }
    }

    if (-1 < idx) {
      const url = `/models/${modelId}`;

      axios
        .delete(url)
        .then((r) => {
          if (!r) {
            toast.error("네트워크가 연결이 안되있거나 서버에 연결할 수 없습니다.", {
              transition: toast.TRANSITIONS.SLIDE,
              position: toast.POSITION.TOP_CENTER,
            });
            return;
          }

          models.value.splice(idx, 1);
          CommonStore().closeGlobalModal();
          EstimateStore().calculateTotalCost();
        })
        .catch((e) => {
          console.log(e);
        });
    }
  }

  function clear() {
    models.value = new Array<Model3D>();
  }

  async function setModelCount(modelId: string, count: number) {
    for (let m = 0; m < models.value.length; m++) {
      const model: Model3D = models.value[m];
      if (modelId === model.id) {
        model.modelCount = count;
        calculateCost(model);
      }
    }
  }

  function setModelTotalCost(modelId: string, cost: number) {
    for (let m = 0; m < models.value.length; m++) {
      const model: Model3D = models.value[m];
      if (modelId === model.id) {
        model.modelTotalCost = cost;
      }
    }
  }

  async function setModelCost(modelId: string, cost: number) {
    const res = await axios.put(`/models/${modelId}`, {modelCost: cost});

    if (200 === res.status) {
      for (let m = 0; m < models.value.length; m++) {
        const model: Model3D = models.value[m];
        if (modelId === model.id) {
          model.modelCost = cost;
          model.modelTotalCost = cost * model.modelCount;
        }
      }
    }
  }

  function setModelPaint(modelId: string, status: boolean) {
    for (let m = 0; m < models.value.length; m++) {
      const model: Model3D = models.value[m];
      if (modelId === model.id) {
        model.modelPaint = status;
      }
    }
  }

  function setModelProcess(modelId: string, processId: number) {
    for (let m = 0; m < models.value.length; m++) {
      const model: Model3D = models.value[m];
      if (modelId === model.id) {
        model.modelProcessId = processId;
      }
    }
  }

  function setModelMaterial(modelId: string, materialId: number) {
    for (let m = 0; m < models.value.length; m++) {
      const model: Model3D = models.value[m];
      if (modelId === model.id) {
        model.modelMaterialId = materialId;
        calculateCost(model);
      }
    }
  }

  function checkExistByName(name: string) {
    for (let m = 0; m < models.value.length; m++) {
      if (name.normalize() === models.value[m].name.normalize()) {
        return true;
      }
    }
  }

  function setThumbnail(sModel: Model3D) {
    const ext = sModel.name.slice(sModel.name.lastIndexOf(".") + 1).toLowerCase();

    for (let m = 0; m < models.value.length; m++) {
      const model: Model3D = models.value[m];
      if (sModel.id === model.id) {
        const key = sModel.modelKey;
        // if ("stp" === ext || "STP" === ext) {
        //   key += ".stp.stl";
        // }
        // key += ".png";

        model.thumbnailUrl = `https://s3.ap-northeast-2.amazonaws.com/${process.env.VUE_APP_IMAGE_BUCKET}/${key}`;
      }
    }
  }

  async function reqGetAnalysis(modelId: string, currentProcess: Process) {
    return new Promise((resolve, reject) => {
      axios
        .get(`/models/${modelId}/analysis`, {
          params: {
            processId: currentProcess.id,
          },
        })
        .then((r) => {
          for (let m = 0; m < models.value.length; m++) {
            const model: Model3D = models.value[m];
            if (modelId === model.id) {
              model.analysis = r.data.result.analysis;
              model.analysisRaw = r.data.result.analysisRaw;

              return resolve(true);
            }
          }

          return reject(false);
        });
    });
  }

  async function reqActiveModel(srcModel: Model3D) {
    return new Promise((resolve, reject) => {
      axios
        .post(`/models/${srcModel.id}/active`, {
          materialId: srcModel.modelMaterialId,
          paint: srcModel.modelPaint,
        })
        .then((r) => {
          for (let m = 0; m < models.value.length; m++) {
            const model: Model3D = models.value[m];
            if (srcModel.id === model.id) {
              const rModel = r.data.result.model;

              if (501 === r.data.status) {
                model.modelAnalysis = rModel.modelAnalysis;
                return resolve(true);
              }

              model.modelUnit = rModel.modelUnit;
              model.modelSizeX = rModel.modelSizeX;
              model.modelSizeY = rModel.modelSizeY;
              model.modelSizeZ = rModel.modelSizeZ;
              model.modelVolume = rModel.modelVolume;
              model.modelAnalysis = rModel.modelAnalysis;
              // model.modelCost = rModel.modelCost;
              // model.modelCount = rModel.modelCount;
              // if (rModel.modelCompanyId) model.modelCompanyId = rModel.modelCompanyId;
              model.modelMaterialId = srcModel.modelMaterialId;
              return resolve(true);
            }
          }

          return reject(false);
        });
    });
  }

  function setCurrentModel(model: Model3D) {
    currentModel.value = model;
  }

  function removeAnalysisRaw() {
    for (let m = 0; m < models.value.length; m++) {
      models.value[m]["analysisRaw"] = [];
    }
  }

  async function setActivedModel(modelId: string) {
    const optionsStore = OptionsStore();

    for (let m = 0; m < models.value.length; m++) {
      const model: Model3D = models.value[m];
      if (modelId === model.id) {
        await ModelStore().reqActiveModel(model);
        await ModelStore().reqGetAnalysis(model.id, optionsStore.firstProcess);
        model.isUploaded = true;
        if (AnalysisStatus.Fail === model.modelAnalysis) {
          return;
        }

        calculateCost(model);

        createTippySingle(
          "#btn_" + model.id + "_analysis",
          "분석결과를 확인할 수 있습니다.",
          "bottom"
        );
      }
    }
  }

  // 2024.08.19 계산식 수정 요청 밴파킹 - 서버에서 계산하도록 변경
  async function calculateCost(model: Model3D) {
    console.log("calculate model cost from server");

    try {
      const response = await axios.post(`/models/${model.id}/calculate-cost`, {
        materialId: model.modelMaterialId,
        paint: model.modelPaint,
        count: model.modelCount,
      });

      if (response.data.status === 100) {
        const result = response.data.result;
        model.modelCost = result.cost;
        model.modelTotalCost = result.modelTotalCost;
        model.modelCompanyId = result.companyId;
        model.modelPackingHeight = result.packingHeight;

        if (result.isUnableSize) {
          console.warn("Model size is too large for available companies");
        }
      } else {
        console.error("Failed to calculate cost from server");
      }
    } catch (error) {
      console.error("Error calculating cost from server:", error);
    }

    EstimateStore().calculateTotalCost();
  }

  return {
    models,
    currentModel,
    totalCost,
    totalQua,
    getCurrentModel,
    createModel,
    addModel,
    removeModel,
    clear,
    setModelCount,
    setModelTotalCost,
    setModelCost,
    setModelProcess,
    setModelMaterial,
    setModelPaint,
    setCurrentModel,
    setThumbnail,
    checkExistByName,
    reqGetAnalysis,
    reqActiveModel,
    removeAnalysisRaw,
    setActivedModel,
    calculateCost,
  };
});
